import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { authApi, errorMessage, publicRequest } from '@/lib/api';
import { clearSession, decodeClaims, readSession, writeSession } from '@/lib/tokens';
import type { Company, RbacRole, User } from '@/types';

export interface AuthState {
  user: User | null;
  company: Company | null;
  role: RbacRole | null;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated';
  error: string | null;
  /** True until the initial session restore finishes, so routes do not flash. */
  initialising: boolean;
}

const claims = readSession() ? decodeClaims(readSession()!.accessToken) : null;

const initialState: AuthState = {
  user: null,
  company: null,
  role: (claims?.app_metadata?.rbac_role as RbacRole) ?? null,
  status: 'idle',
  error: null,
  initialising: Boolean(readSession()),
};

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
  company: Company | null;
}

export const login = createAsyncThunk<AuthResponse, { email: string; password: string }, { rejectValue: string }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await authApi.post<AuthResponse>('/auth/login', credentials, publicRequest);
      writeSession(data);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not sign you in'));
    }
  }
);

export interface SignupPayload {
  email: string;
  password: string;
  fullName: string;
  accountType: 'employer' | 'candidate';
  companyName?: string;
  jobTitle?: string;
}

export const signup = createAsyncThunk<AuthResponse, SignupPayload, { rejectValue: string }>(
  'auth/signup',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await authApi.post<AuthResponse>('/auth/signup', payload, publicRequest);
      writeSession(data);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not create your account'));
    }
  }
);

/** Restores the signed-in user on a page load from the stored refresh token. */
export const restoreSession = createAsyncThunk<
  { user: User; company: Company | null },
  void,
  { rejectValue: string }
>('auth/restore', async (_, { rejectWithValue }) => {
  try {
    const { data } = await authApi.get<{ user: User; company: Company | null }>('/auth/me');
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Session expired'));
  }
});

export const updateProfile = createAsyncThunk<User, Partial<User>, { rejectValue: string }>(
  'auth/updateProfile',
  async (patch, { rejectWithValue }) => {
    try {
      const { data } = await authApi.patch<User>('/auth/me', patch);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not save your profile'));
    }
  }
);

export const updateCompany = createAsyncThunk<Company, Partial<Company>, { rejectValue: string }>(
  'auth/updateCompany',
  async (patch, { rejectWithValue }) => {
    try {
      const { data } = await authApi.patch<Company>('/company', patch);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not save the company profile'));
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  const session = readSession();
  if (session) {
    // Best effort — the local session is cleared either way.
    await authApi.post('/auth/logout', { refreshToken: session.refreshToken }, publicRequest).catch(() => undefined);
  }
  clearSession();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    sessionLost(state) {
      clearSession();
      state.user = null;
      state.company = null;
      state.role = null;
      state.status = 'unauthenticated';
      state.initialising = false;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const authenticated = (state: AuthState, action: PayloadAction<AuthResponse>) => {
      state.user = action.payload.user;
      state.company = action.payload.company;
      state.role = action.payload.user.rbacRole;
      state.status = 'authenticated';
      state.initialising = false;
      state.error = null;
    };

    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, authenticated)
      .addCase(login.rejected, (state, action) => {
        state.status = 'unauthenticated';
        state.error = action.payload ?? 'Could not sign you in';
      })
      .addCase(signup.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(signup.fulfilled, authenticated)
      .addCase(signup.rejected, (state, action) => {
        state.status = 'unauthenticated';
        state.error = action.payload ?? 'Could not create your account';
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.company = action.payload.company;
        state.role = action.payload.user.rbacRole;
        state.status = 'authenticated';
        state.initialising = false;
      })
      .addCase(restoreSession.rejected, (state) => {
        clearSession();
        state.status = 'unauthenticated';
        state.initialising = false;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.company = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.company = null;
        state.role = null;
        state.status = 'unauthenticated';
      });
  },
});

export const { sessionLost, clearError } = authSlice.actions;
export default authSlice.reducer;
